import { useRef, useState, useEffect, type ReactNode, type CSSProperties } from 'react'

interface GlowCardProps {
  children: ReactNode
  style?: CSSProperties
  className?: string
  onClick?: () => void
  delay?: number
}

export function GlowCard({ children, style, className, onClick, delay = 0 }: GlowCardProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [mouse, setMouse] = useState({ x: 0, y: 0, active: false })
  const [hovered, setHovered] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect()
      setMouse({ x: e.clientX - rect.left, y: e.clientY - rect.top, active: true })
    }
    const onLeave = () => setMouse(prev => ({ ...prev, active: false }))
    el.addEventListener('mousemove', onMove)
    el.addEventListener('mouseleave', onLeave)
    return () => {
      el.removeEventListener('mousemove', onMove)
      el.removeEventListener('mouseleave', onLeave)
    }
  }, [])

  return (
    <div
      ref={ref}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={className}
      style={{
        position: 'relative',
        background: 'var(--bg-1)',
        border: `1px solid ${hovered ? 'var(--border-h)' : 'var(--border)'}`,
        borderRadius: 'var(--r-l)',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all var(--dur-n) var(--ease)',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? 'var(--sh-md)' : 'none',
        animation: delay > 0 ? `in-up 600ms var(--ease) ${delay}ms both` : undefined,
        ...style,
      }}
    >
      {/* Mouse-tracking glow — THE signature V3 effect */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          background: mouse.active
            ? `radial-gradient(400px circle at ${mouse.x}px ${mouse.y}px, var(--accent-s), transparent 60%)`
            : 'none',
          opacity: hovered ? 1 : 0,
          transition: 'opacity var(--dur-n)',
          zIndex: 0,
        }}
      />
      {/* Content — always above glow */}
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </div>
  )
}
